import { computed, watch } from "vue";
import type { Ref } from "vue";
import {
  getRestaurantDateKey,
  getRestaurantDateRangeUtc,
} from "@lemmonhq/blogic";
import { getCurrentShiftFilters } from "../../../utils/shiftHelpers";
import type { ReservationSchedule } from "../../../utils/shiftHelpers";
import type { TabValue, ViewMode } from "../config";
import { useReservationCounts } from "@app/vue/composables/useReservationCounts";
import { useShop } from "@app/vue/providers/ShopConnection";

export function useTabCounts(params: {
  selectedDate: Ref<Date | null>;
  viewMode: Ref<ViewMode>;
  restaurantTimeZone: Ref<string>;
  reservationSchedule: Ref<ReservationSchedule | null>;
  activeTab: Ref<TabValue>;
}) {
  const shop = useShop();
  const {
    selectedDate,
    viewMode,
    restaurantTimeZone,
    reservationSchedule,
    activeTab,
  } = params;

  // Confirmed window:
  //  - When the confirmed tab is active, follow the date picker + viewMode so
  //    the badge matches what the list is showing.
  //  - Otherwise show today only so the badge is a "live today" indicator
  //    regardless of which date the manager has browsed to on another tab.
  const confirmedDateFrom = computed<string | undefined>(() => {
    const tz = restaurantTimeZone.value;
    if (activeTab.value === "confirmed" && selectedDate.value) {
      const vm = viewMode.value;
      if (vm === "all") {
        // "All" means from today forward
        const today = getRestaurantDateKey(new Date(), tz);
        return getRestaurantDateRangeUtc(today, "day", tz).date_from;
      }
      const key = getRestaurantDateKey(selectedDate.value, tz);
      return getRestaurantDateRangeUtc(key, vm, tz).date_from;
    }
    // Default: today
    const today = getRestaurantDateKey(new Date(), tz);
    return getRestaurantDateRangeUtc(today, "day", tz).date_from;
  });

  const confirmedDateTo = computed<string | undefined>(() => {
    const tz = restaurantTimeZone.value;
    if (activeTab.value === "confirmed" && selectedDate.value) {
      const vm = viewMode.value;
      if (vm === "all") return undefined; // no upper bound for "all"
      const key = getRestaurantDateKey(selectedDate.value, tz);
      return getRestaurantDateRangeUtc(key, vm, tz).date_to;
    }
    // Default: today
    const today = getRestaurantDateKey(new Date(), tz);
    return getRestaurantDateRangeUtc(today, "day", tz).date_to;
  });

  // Seated window: always pinned to the current shift — never follows the picker.
  const seatedDateFrom = computed<string | undefined>(() => {
    const shift = getCurrentShiftFilters(
      new Date(),
      reservationSchedule.value,
      restaurantTimeZone.value,
    );
    return shift.date_from;
  });

  const seatedDateTo = computed<string | undefined>(() => {
    const shift = getCurrentShiftFilters(
      new Date(),
      reservationSchedule.value,
      restaurantTimeZone.value,
    );
    return shift.date_to;
  });

  const counts = useReservationCounts({
    confirmedDateFrom,
    confirmedDateTo,
    seatedDateFrom,
    seatedDateTo,
  });

  // Shape expected by PageHeader: Record<tab, { reservations, people }>
  const tabCounts = computed(() => ({
    confirmed: {
      reservations: counts.confirmed.value,
      people: counts.confirmedPeople.value,
    },
    seated: {
      reservations: counts.seated.value,
      people: counts.seatedPeople.value,
    },
    pending: {
      reservations: counts.pending.value,
      people: counts.pendingPeople.value,
    },
  }));
  // Refetch counts whenever activeTab changes
  watch(activeTab, () => {
    void counts.refresh();
  });
  watch(shop.pendingReservationRequestsCount, () => {
    void counts.refresh();
  });

  return { tabCounts, refreshTabCounts: counts.refresh };
}
